import React, {
  createContext,
  useState,
  useMemo,
  ReactNode,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import * as Sentry from "@sentry/react";
import { UserProfile, UserRole } from "../types";
import { supabase, isSupabaseConfigured, dbReady } from "../lib/supabase";
import { logger } from "../lib/logger";
import { logAudit, flushAuditLogs } from "../lib/auditLog";

// =============================================================================
// AuthContext - Authentication & Session Management
// =============================================================================
// PURPOSE: Manages user authentication, session state, role normalization, and
// view-as impersonation for executives.
//
// ARCHITECTURE:
// - Session via onAuthStateChange ONLY — never call getSession()/getUser() for init
//   (prevents race with Supabase's internal _recoverAndRefresh which can delete sessions)
// - Role normalization: converts legacy role names to standard UserRole values
// - View-as impersonation: executives can view platform as any team member
// - 10-day inactivity timeout with activity tracking (sliding window)
// - Profile fetch with retry on cold start (handles Supabase wake-up latency)
//
// KEY DECISIONS:
// - INITIAL_SESSION event is the sole entry point for session restoration
// - Profile fetch deferred with setTimeout(0) to prevent auth-js lock deadlocks
// - Retry logic on auth failure: waits for DB warm-up before failing login
// - Real user vs effective user: stores both for impersonation tracking
// - Role priority system: ROLE_PRIORITY determines highest privilege when user
//   has multiple org memberships
// - Security: stores realUser separately to prevent impersonation abuse
// =============================================================================

export interface ViewAsUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  sector?: string;
  podId?: string;
  ghlUserId?: string;
  displayCurrency?: string | null; // target's profiles.display_currency — so View As shows their currency POV
}

interface AuthContextType {
  user: UserProfile | null;
  realUser: UserProfile | null;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ error: unknown }>;
  logout: () => Promise<void>;
  isLoading: boolean;
  authError: string | null;
  viewAsUser: ViewAsUser | null;
  setViewAsUser: (override: ViewAsUser | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Priority Score: Higher number = Higher Privilege
const ROLE_PRIORITY: Record<string, number> = {
  exec: 100,
  client: 80,
  manager: 70,
  closer: 50,
  setter: 50,
  dialer: 30,
  viewer: 10,
};

export const normalizeRole = (rawRole: string): UserRole => {
  const r = rawRole?.toLowerCase().trim() || "";
  if (
    [
      "super admin",
      "superadmin",
      "ceo",
      "founder",
      "owner",
      "administrator",
      "admin",
      "exec",
    ].includes(r)
  )
    return "exec";
  if (["manager", "team lead", "general manager"].includes(r)) return "manager";
  if (["client owner", "client_owner", "client"].includes(r)) return "client";
  if (["closer", "sales rep", "account executive"].includes(r)) return "closer";
  if (["setter", "sdr", "bdr"].includes(r)) return "setter";
  if (r === "dialer") return "dialer";
  return "viewer";
};

/** Derive sector from role. Only closer/setter/dialer have a sector. */
export const deriveSectorFromRole = (role: UserRole): string | undefined => {
  if (role === "closer" || role === "setter" || role === "dialer") return role;
  return undefined;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [realUser, setRealUser] = useState<UserProfile | null>(null);
  const realUserRef = useRef<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [viewAsUser, setViewAsUser] = useState<ViewAsUser | null>(null);

  // --- Session Timeout Configuration ---
  // 10 days of inactivity before auto-logout. Supabase handles token refresh
  // automatically via autoRefreshToken: true in the client config.
  // Last activity is persisted to localStorage so the timer survives page refreshes.
  // Use a ref (not state) — this value is only needed for timeout checks and
  // localStorage writes. It never needs to trigger a re-render.
  const IDLE_TIMEOUT_MS = 10 * 24 * 60 * 60 * 1000; // 10 days
  const lastActivityRef = useRef(
    (() => {
      const stored = localStorage.getItem("altara_last_activity");
      return stored ? Number(stored) : Date.now();
    })()
  );

  // Effective user: if exec is impersonating, merge override into user profile
  const user = useMemo<UserProfile | null>(() => {
    if (!realUser) return null;
    if (realUser.role !== "exec" || !viewAsUser) return realUser;
    return {
      ...realUser,
      id: viewAsUser.id,
      name: viewAsUser.name,
      email: viewAsUser.email,
      role: viewAsUser.role,
      sector: viewAsUser.sector,
      podId: viewAsUser.podId,
      ghlUserId: viewAsUser.ghlUserId,
      // Currency follows the impersonated identity so "View As Rayston" shows
      // Rayston's SGD (Mako 2026-07-16). null = the target sees USD.
      displayCurrency: viewAsUser.displayCurrency ?? null,
    };
  }, [realUser, viewAsUser]);

  // --- Auth Functions (defined early for use in useEffects) ---
  const logout = useCallback(async () => {
    // Log logout event before clearing session
    logAudit("logout");
    flushAuditLogs(); // Immediate flush on logout

    Sentry.setUser(null); // Clear Sentry user context

    if (supabase) await supabase.auth.signOut();
    setRealUser(null);
    setViewAsUser(null);
    setAuthError(null);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setAuthError(null);
    if (!supabase) return { error: { message: "Supabase not configured" } };

    const result = await supabase.auth.signInWithPassword({ email, password });

    // Log login event and reset activity timer if successful
    if (!result.error && result.data?.session) {
      logAudit("login", undefined, undefined, { email });
      // Reset 10-day sliding window on fresh login
      const now = Date.now();
      localStorage.setItem("altara_last_activity", String(now));
      lastActivityRef.current = now;
    }

    return result;
  }, []);

  // --- Fetch User Profile & Roles ---
  const fetchUserProfile = async (sessionEmail: string, retryCount = 0) => {
    if (!supabase) return;

    if (retryCount === 0) {
      setAuthError(null);
      logger.debug("Auth: Fetching profile");
    } else {
      logger.debug(`Auth: Retry attempt ${retryCount}`);
    }

    try {
      // Wait for warm-up ping to finish so we don't query a cold DB
      await dbReady;

      // Fetch profile + memberships in parallel (single round-trip)
      const fetchData = async () => {
        const profileQuery = supabase!
          .from("profiles")
          .select("*")
          .eq("email", sessionEmail)
          .single();

        const membershipsQuery = supabase!
          .from("organization_members")
          .select("organization_id, role, pod_id, user_id")
          .eq("status", "active")
          .limit(100); // Safety cap: user can be member of max 100 orgs

        const [profileResult, membershipsResult] = await Promise.all([
          profileQuery,
          membershipsQuery,
        ]);

        if (profileResult.error || !profileResult.data) {
          throw new Error("User profile not found in 'profiles' table.");
        }

        interface MembershipRow {
          organization_id: string;
          role: string;
          pod_id: string | null;
          user_id: string;
        }

        // Filter memberships client-side (we need profile.id which we now have)
        const memberships = ((membershipsResult.data as MembershipRow[]) || []).filter(
          (m) => m.user_id === profileResult.data.id
        );

        return { profile: profileResult.data, memberships };
      };

      const timeout = new Promise((_, reject) => {
        setTimeout(() => {
          reject(
            new Error(
              "Database request timed out (15s). Connection is slow or Supabase is waking up."
            )
          );
        }, 15000);
      });

      interface FetchDataResult {
        profile: {
          id: string;
          full_name: string | null;
          email: string;
          role: string | null;
          is_super_admin: boolean;
          avatar_url: string | null;
          ghl_user_id: string | null;
          display_currency: string | null;
        };
        memberships: Array<{
          organization_id: string;
          role: string;
          pod_id: string | null;
          user_id: string;
        }>;
      }

      const result = (await Promise.race([fetchData(), timeout])) as FetchDataResult;
      const { profile, memberships } = result;

      // 4. Construct User Object
      const orgRoles: Record<string, UserRole> = {};
      let highestRole: UserRole = "viewer";
      let userPodId: string | undefined;
      let userSector: string | undefined;
      let primaryOrgId: string | undefined;

      // Calculate highest role from organization memberships
      if (memberships && memberships.length > 0) {
        memberships.forEach((m) => {
          const role = normalizeRole(m.role);
          if (m.organization_id) orgRoles[m.organization_id] = role;

          if (ROLE_PRIORITY[role] > ROLE_PRIORITY[highestRole]) {
            highestRole = role;
            primaryOrgId = m.organization_id;
          }

          // Capture pod_id from membership; sector is derived from role
          if (m.pod_id && !userPodId) userPodId = m.pod_id;
          if (!userSector) userSector = deriveSectorFromRole(role);
        });
      }

      // Check for Global Role Override in Profile
      if (profile.is_super_admin) {
        highestRole = "exec";
      } else if (profile.role) {
        const globalRole = normalizeRole(profile.role);
        if (ROLE_PRIORITY[globalRole] > ROLE_PRIORITY[highestRole]) {
          highestRole = globalRole;
        }
      }

      // 5. Fetch managed pod IDs for managers
      // Managers see only their pods' data — query pods WHERE manager_id = userId.
      // Exec/super_admin: set empty (they use canViewAllPods instead).
      // Reps: set empty (they use hasTunnelVision / podId).
      let managedPodIds: string[] = [];
      const isManagerRole = highestRole === "manager";
      if (isManagerRole && primaryOrgId && supabase) {
        const { data: managedPods } = await supabase
          .from("pods")
          .select("id")
          .eq("manager_id", profile.id)
          .eq("organization_id", primaryOrgId);
        managedPodIds = (managedPods || []).map((p: { id: string }) => p.id);
      }

      const finalProfile: UserProfile = {
        id: profile.id,
        name: profile.full_name || sessionEmail.split("@")[0],
        email: sessionEmail,
        role: highestRole,
        orgRoles: orgRoles,
        avatar: profile.avatar_url || undefined,
        podId: userPodId,
        managedPodIds,
        sector: userSector,
        ghlUserId: profile.ghl_user_id || undefined,
        isSuperAdmin: profile.is_super_admin, // GOD tier (Mako + Nathaniel) — gates SOP editing
        displayCurrency: profile.display_currency ?? null, // per-user display currency (drives the toggle)
      };

      realUserRef.current = finalProfile;
      setRealUser(finalProfile);

      // Reset the 10-day sliding window on every successful session restore.
      // Visiting the page = activity, so the timer resets.
      const now = Date.now();
      localStorage.setItem("altara_last_activity", String(now));
      lastActivityRef.current = now;

      // Identify user in Sentry so errors show who was affected
      Sentry.setUser({
        id: finalProfile.id,
        email: finalProfile.email,
        username: finalProfile.name,
      });
      Sentry.setTag("user_role", finalProfile.role);
      const orgIds = Object.keys(finalProfile.orgRoles || {});
      if (orgIds.length > 0) {
        Sentry.setTag("organization_id", orgIds[0]);
      }

      logger.info("Auth Success");
    } catch (err) {
      logger.error(`Profile Fetch Failure (Attempt ${retryCount + 1})`);

      // Retry Logic: Try once more before failing (DB should be warm after first attempt)
      if (retryCount < 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return fetchUserProfile(sessionEmail, retryCount + 1);
      }

      // If we already have a user in state (e.g. token refresh or reconnect),
      // keep them logged in instead of wiping the session on a transient failure.
      setRealUser((prev) => {
        if (prev) {
          logger.warn("Auth: Keeping existing session despite fetch failure");
          return prev;
        }
        const errMessage = err instanceof Error ? err.message : "Unknown Auth Error";
        setAuthError(`Login Failed: ${errMessage}`);
        return null;
      });
    }
  };

  // --- Auth Initialization via onAuthStateChange ONLY ---
  // NEVER call getSession() or getUser() manually for auth init.
  // Both race against Supabase's internal _recoverAndRefresh() which runs
  // during client initialization. If _recoverAndRefresh() fails (non-retryable
  // error like 401), it DELETES the session from localStorage before our code
  // can read it. onAuthStateChange fires AFTER recovery completes, so
  // INITIAL_SESSION is the only safe entry point.
  //
  // See: supabase/gotrue-js#141, supabase/auth-js#213, supabase/auth-js#762
  useEffect(() => {
    let mounted = true;

    if (!isSupabaseConfigured || !supabase) {
      setIsLoading(false);
      return;
    }

    // Check 10-day inactivity BEFORE subscribing.
    // If expired, sign out immediately and don't bother with session recovery.
    const storedActivity = localStorage.getItem("altara_last_activity");
    if (storedActivity) {
      const idleTime = Date.now() - Number(storedActivity);
      if (idleTime >= IDLE_TIMEOUT_MS) {
        logger.info("Session expired: inactive for 10+ days");
        localStorage.removeItem("altara_last_activity");
        supabase.auth.signOut();
        setRealUser(null);
        setIsLoading(false);
        return;
      }
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      logger.debug("Auth event", { event });

      if (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        if (session?.user?.email) {
          // TOKEN_REFRESHED fires on every tab focus — skip profile re-fetch
          // if we already have the user loaded. The token is already refreshed
          // in the SDK; re-fetching profile just triggers a cascade:
          // new realUser obj → DataContext re-fetches orgs → TeamContext re-fetches
          // 5 heavy queries → 3-second UI freeze.
          if (event === "TOKEN_REFRESHED" && realUserRef.current) {
            if (mounted) setIsLoading(false);
            return;
          }
          // Defer profile fetch with setTimeout to prevent deadlocks.
          // Calling Supabase operations inside onAuthStateChange synchronously
          // can deadlock because the listener holds an internal lock.
          // See: supabase/auth-js#762
          setTimeout(() => {
            if (!mounted) return;
            fetchUserProfile(session.user!.email!).then(() => {
              if (mounted) setIsLoading(false);
            });
          }, 0);
        } else if (event === "INITIAL_SESSION") {
          // INITIAL_SESSION with no session = not logged in
          logger.debug("Auth: No session (INITIAL_SESSION)");
          setRealUser(null);
          setIsLoading(false);
        }
      } else if (event === "SIGNED_OUT") {
        realUserRef.current = null;
        setRealUser(null);
        setViewAsUser(null);
        setIsLoading(false);
        setAuthError(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Track user activity — throttled to avoid spamming localStorage on every event.
  // Writes directly to lastActivityRef.current — no state update, no re-render.
  useEffect(() => {
    let lastWrite = 0;
    const updateActivity = () => {
      const now = Date.now();
      // Only persist every 60s to avoid excessive localStorage writes
      if (now - lastWrite > 60_000) {
        lastWrite = now;
        localStorage.setItem("altara_last_activity", String(now));
      }
      lastActivityRef.current = now;
    };

    window.addEventListener("mousedown", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("scroll", updateActivity);
    window.addEventListener("touchstart", updateActivity);

    return () => {
      window.removeEventListener("mousedown", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("scroll", updateActivity);
      window.removeEventListener("touchstart", updateActivity);
    };
  }, []);

  // Check for session timeout - uses ref to avoid recreating interval on every interaction
  useEffect(() => {
    if (!realUser) return;

    const checkTimeout = setInterval(
      () => {
        const idleTime = Date.now() - lastActivityRef.current;
        if (idleTime >= IDLE_TIMEOUT_MS) {
          logger.info("Session timed out due to inactivity");
          logAudit("logout", undefined, undefined, {
            reason: "session_timeout",
            idle_minutes: Math.floor(idleTime / 60000),
          });
          flushAuditLogs(); // Immediate flush on timeout
          logout();
        }
      },
      60 * 60 * 1000
    ); // Check every hour (timeout is 10 days, no need for minute-level checks)

    return () => clearInterval(checkTimeout);
  }, [realUser, logout]); // Only recreate when user or logout changes

  // Memoize context value — prevents all consumers from re-rendering on unrelated
  // state changes (e.g. internal refs, throttled activity updates).
  const contextValue = useMemo(
    () => ({
      user,
      realUser,
      isAuthenticated: !!realUser,
      signIn,
      logout,
      isLoading,
      authError,
      viewAsUser,
      setViewAsUser,
    }),
    [user, realUser, signIn, logout, isLoading, authError, viewAsUser, setViewAsUser]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
